import React, { Component } from 'react';
import { styled } from '@compiled/react';
import debounce from 'lodash/debounce';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { borderRadius } from '@atlassian/jira-common-styles/src/main.tsx';
import type { IssueId } from '@atlassian/jira-shared-types/src/general.tsx';
import type { Item, ItemProject } from '../../../model/table';
import type { TimelineDuration, TimelineWindow, TimelineMode } from '../../../model/timeline';
import { RESIZE_DEBOUNCE_MS } from '../common/constants';
import ViewportProvider from '../common/context/viewport/provider/index.tsx';
import type { TableCommonProps, OnItemExpandChanged } from '../common/types';
import type { Components } from '../components';
import getComponentsWithDefaults from './utils/get-components-with-defaults.tsx';
import getMaxListWidth from './utils/get-max-list-width.tsx';
import normaliseItems from './utils/normalise-items.tsx';

type Props = {
	isHighlightToday: boolean;
	isHideScrollbar: boolean;
	isResizeEnabled: boolean;
	items: Item[];
	projects: ItemProject[];
	selectedItemId: IssueId | undefined;
	timelineMode: TimelineMode;
	timelineDuration: TimelineDuration;
	timelineWindow: TimelineWindow;
	timelineDefaultPosition: number;
	today: Date;
} & TableCommonProps & {
		components: Partial<Components>;
		listWidth: number;
		onTimelineUpdated: () => void;
		onItemExpandChanged: OnItemExpandChanged;
		onSetListWidth: (width: number) => void;
	};

type State = {
	maxListWidth: number;
};

// eslint-disable-next-line jira/react/no-class-components
class TableMain extends Component<Props, State> {
	constructor(props: Props) {
		super(props);

		this.state = {
			maxListWidth: props.listWidth,
		};
	}

	componentDidMount() {
		if (this.props.isResizeEnabled) {
			this.updateMaxListWidth();
			window.addEventListener('resize', this.onWindowResize);
		}
	}

	componentWillUnmount() {
		if (this.props.isResizeEnabled) {
			window.removeEventListener('resize', this.onWindowResize);
		}
	}

	onWindowResize = debounce(() => {
		this.updateMaxListWidth();
	}, RESIZE_DEBOUNCE_MS);

	onSetTableRef = (elem: HTMLElement | null) => {
		this.tableRef = elem;
	};

	updateMaxListWidth() {
		if (this.tableRef) {
			const tableBoundingClient = this.tableRef.getBoundingClientRect();
			const maxListWidth = getMaxListWidth(tableBoundingClient.width);

			this.setState({ maxListWidth });
		}
	}

	// @ts-expect-error - TS2564 - Property 'tableRef' has no initializer and is not definitely assigned in the constructor.
	tableRef: HTMLElement | null;

	render() {
		const {
			isSelectEnabled,
			isHoverEnabled,
			isResizeEnabled,
			isHighlightToday,
			isHideScrollbar,
			items,
			itemHeight,
			selectedItemId,
			activeLevel,
			expandedItems,
			timelineMode,
			timelineDuration,
			timelineWindow,
			timelineDefaultPosition,
			today,
			listWidth,
			onTimelineUpdated,
			onItemClick,
			onItemExpandChanged,
			onSetListWidth,
			components,
			projects,
		} = this.props;
		const { maxListWidth } = this.state;

		const { listItems, chartItems } = normaliseItems(items);

		const { List, Chart, listComponents, chartComponents } = getComponentsWithDefaults(components);

		return (
			<ViewportProvider>
				<MainTableContainer
					ref={this.onSetTableRef}
					data-testid="deployments.common.components.table.main.table"
				>
					<List
						isResizeEnabled={isResizeEnabled}
						isSelectEnabled={isSelectEnabled}
						isHoverEnabled={isHoverEnabled}
						selectedItemId={selectedItemId}
						items={listItems}
						itemHeight={itemHeight}
						expandedItems={expandedItems}
						activeLevel={activeLevel}
						listWidth={listWidth}
						maxListWidth={maxListWidth}
						// @ts-expect-error - TS2322 - Type '{ ListItem: ComponentType<Flow.Diff<Record<any, any> | (Record<any, any> & { children?: ReactNode; }), InjectIntlVoidProps>> | ListItemComponentType; ListItemContent: (({ id }: APIProps) => JSX.Element) | ListItemContentComponentType; NoMatches: NoMatchesComponentType | (() => null); Loader: ChartLoaderComponentType...' is not assignable to type 'ListComponents'.
						components={listComponents}
						onItemClick={onItemClick}
						onItemExpandChanged={onItemExpandChanged}
						onSetListWidth={onSetListWidth}
						projects={projects}
					/>
					<Chart
						onTimelineUpdated={onTimelineUpdated}
						isSelectEnabled={isSelectEnabled}
						isHoverEnabled={isHoverEnabled}
						isHighlightToday={isHighlightToday}
						isHideScrollbar={isHideScrollbar}
						items={chartItems}
						itemHeight={itemHeight}
						expandedItems={expandedItems}
						activeLevel={activeLevel}
						listWidth={listWidth}
						today={today}
						timelineMode={timelineMode}
						timelineDuration={timelineDuration}
						timelineWindow={timelineWindow}
						timelineDefaultPosition={timelineDefaultPosition}
						components={chartComponents}
						onItemClick={onItemClick}
					/>
				</MainTableContainer>
			</ViewportProvider>
		);
	}
}

const primaryBorder = `1px solid ${token('color.border', colors.N30)}`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MainTableContainer = styled.div({
	display: 'flex',
	position: 'absolute',
	top: 0,
	right: 0,
	bottom: 0,
	left: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${borderRadius}px`,
	border: primaryBorder,
});

export default TableMain;
